'use client';

import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import Link from "next/link";

import { UserAuthForm, UserAuthFormProps } from "@/components/auth/SignInForm";
import { Progress } from "@/components/ui/progress";
import { paths } from "@/app/paths";
import { cn } from "@/lib/utils";
import { waitForAuthenticatedSession } from "@/utils/auth/client-session";
import { useEventTracking } from '@/hooks/useEventTracking';
import { EventType } from '@/api/types/events';

interface SignInComponentProps extends UserAuthFormProps {
    onSuccess?: (action?: () => void) => void;
    title?: string;
    description?: string;
    fullHeight?: boolean;
    className?: string;
}

export const SignInComponent = ({
    mode = "sign-in",
    onSuccess,
    title,
    description,
    fullHeight = true,
    className,
}: SignInComponentProps) => {
    const { status, update } = useSession();
    const { trackEvent } = useEventTracking({
        autoTrackPageViews: false,
        autoTrackScrollDepth: false,
    });
    const router = useRouter();
    const didHandleAuthRef = useRef(false);
    const authEventTrackedRef = useRef(false);

    const [modeType, setModeType] = useState<"sign-in" | "sign-up">(mode);
    const [isFinalizingSignIn, setIsFinalizingSignIn] = useState(false);
    const [signInSyncProgress, setSignInSyncProgress] = useState(0);
    const [signInSyncError, setSignInSyncError] = useState<string | null>(null);

    const handleAuthenticated = useCallback(() => {
        if (didHandleAuthRef.current) {
            return;
        }
        didHandleAuthRef.current = true;

        if (!authEventTrackedRef.current) {
            authEventTrackedRef.current = true;
            const authEventType = modeType === 'sign-up' ? EventType.SIGNUP : EventType.LOGIN;
            void trackEvent({
                event_type: authEventType,
                element_id: 'auth-complete',
                event_details: {
                    auth_mode: modeType,
                    surface: 'sign_in_component',
                },
            });
        }

        if (onSuccess) {
            onSuccess();
            return;
        }

        router.replace(paths.dashboard());
    }, [onSuccess, router, modeType, trackEvent]);

    const waitForSyncedSession = useCallback(async () => {
        setIsFinalizingSignIn(true);
        setSignInSyncError(null);
        setSignInSyncProgress(12);

        const synced = await waitForAuthenticatedSession(
            typeof update === "function" ? update : undefined,
            {
                maxAttempts: 12,
                intervalMs: 250,
                onAttempt: (attempt, maxAttempts) => {
                    const ratio = attempt / maxAttempts;
                    const nextProgress = Math.min(96, Math.round(12 + ratio * 80));
                    setSignInSyncProgress((current) => Math.max(current, nextProgress));
                },
            }
        );

        if (!synced) {
            setIsFinalizingSignIn(false);
            setSignInSyncProgress(0);
            setSignInSyncError("We couldn't confirm your session yet. Please try again.");
            return false;
        }

        setSignInSyncProgress(100);
        setIsFinalizingSignIn(false);
        return true;
    }, [update]);

    useEffect(() => {
        if (status === "authenticated") {
            handleAuthenticated();
        }
    }, [status, handleAuthenticated]);

    useEffect(() => {
        const handleAuthSuccess = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data !== 'auth-success') return;

            void (async () => {
                const isSessionReady = await waitForSyncedSession();
                if (!isSessionReady) {
                    return;
                }
                handleAuthenticated();
            })();
        };

        window.addEventListener('message', handleAuthSuccess);
        return () => window.removeEventListener('message', handleAuthSuccess);
    }, [handleAuthenticated, waitForSyncedSession]);

    const defaultHeadline =
        modeType === "sign-in"
            ? "Sign in to keep your offensive going."
            : "Start your offensive.";
    const defaultSubhead =
        modeType === "sign-in"
            ? "Your reports, contacts, and conversations — exactly where you left them."
            : "Build your target list, find the right people, and run real outreach. Free to start.";

    return (
        <div
            className={cn(
                "relative mx-auto w-full max-w-[420px] px-6 py-8 sm:px-0 sm:py-10",
                {
                    "min-h-screen flex items-center": fullHeight,
                    "min-h-0": !fullHeight,
                },
                className
            )}
        >
            <div className="flex w-full flex-col gap-6">
                <div className="flex flex-col gap-2 text-left">
                    <h1 className="text-title-lg text-foreground">
                        {title || defaultHeadline}
                    </h1>
                    <p className="text-body text-muted-foreground">
                        {description || defaultSubhead}
                    </p>
                    <p className="text-body-sm text-muted-foreground">
                        {modeType === "sign-in" ? (
                            <>
                                New here?{" "}
                                <button
                                    onClick={() => setModeType("sign-up")}
                                    className="font-semibold text-primary underline-offset-2 hover:underline"
                                >
                                    Create an account
                                </button>{" "}
                                — same form.
                            </>
                        ) : (
                            <>
                                Already have an account?{" "}
                                <button
                                    onClick={() => setModeType("sign-in")}
                                    className="font-semibold text-primary underline-offset-2 hover:underline"
                                >
                                    Sign in
                                </button>{" "}
                                — same form.
                            </>
                        )}
                    </p>
                </div>

                {isFinalizingSignIn && (
                    <div className="space-y-2 border border-primary/20 bg-primary/5 px-3 py-3">
                        <p className="text-overline text-primary">
                            Finalizing sign in…
                        </p>
                        <Progress value={signInSyncProgress} className="h-1.5" />
                    </div>
                )}

                {signInSyncError && (
                    <p className="border border-destructive/30 bg-destructive/10 px-3 py-2 text-body-sm text-destructive">
                        {signInSyncError}
                    </p>
                )}

                <UserAuthForm
                    mode={modeType}
                    className={cn(isFinalizingSignIn && "pointer-events-none opacity-60")}
                />

                <p className="text-caption text-muted-foreground">
                    By continuing you agree to our{" "}
                    <Link
                        href={paths.terms()}
                        className="underline underline-offset-2 hover:text-foreground"
                    >
                        Terms
                    </Link>{" "}
                    and{" "}
                    <Link
                        href={paths.privacy()}
                        className="underline underline-offset-2 hover:text-foreground"
                    >
                        Privacy Policy
                    </Link>
                    . Your email goes nowhere except your account. Protected
                    by reCAPTCHA — Google&rsquo;s{" "}
                    <a
                        href="https://policies.google.com/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                    >
                        Privacy
                    </a>{" "}
                    and{" "}
                    <a
                        href="https://policies.google.com/terms"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline underline-offset-2 hover:text-foreground"
                    >
                        Terms
                    </a>{" "}
                    apply.
                </p>
            </div>
        </div>
    );
};

export default SignInComponent;
